import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { PAGINATION_SETTINGS } from "@/constants";
import { getBatchInvoiceSessions } from "@/service/weexpertService";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

const SESSION_STATUS_COLORS = {
  PENDING: "bg-yellow-500",
  SUCCEEDED: "bg-green-500",
  PARTIALLY_SUCCEEDED: "bg-orange-500",
  FAILED: "bg-red-500",
};

export default function BatchInvoiceSessionsPage() {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGINATION_SETTINGS.invoices,
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "batch-invoice-sessions",
      pagination.pageSize,
      pagination.pageIndex,
    ],
    queryFn: () =>
      getBatchInvoiceSessions(
        pagination.pageSize,
        pagination.pageIndex * pagination.pageSize
      ),
  });

  const sessions = data?.data?.sessions ?? [];
  const total = data?.data?.total ?? 0;

  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <Link
            to={`/admin/batch-invoice-session/${row.original.id}`}
            className="text-blue-600 hover:underline"
          >
            #{row.original.id}
          </Link>
        ),
        key: uuidv4(),
      },
      {
        accessorKey: "status",
        header: t("invoices:batch_session_status"),
        cell: ({ row }) => (
          <Badge
            className={
              SESSION_STATUS_COLORS[row.original.status] ?? "bg-gray-500"
            }
          >
            {row.original.status}
          </Badge>
        ),
        key: uuidv4(),
      },
      {
        accessorKey: "relatedDate",
        header: t("invoices:batch_session_related_date"),
        cell: ({ row }) =>
          row.original.relatedDate
            ? format(new Date(row.original.relatedDate), "dd/MM/yyyy")
            : "—",
        key: uuidv4(),
      },
      {
        accessorKey: "createdAt",
        header: t("invoices:batch_session_created_at"),
        cell: ({ row }) =>
          row.original.createdAt
            ? format(new Date(row.original.createdAt), "dd/MM/yyyy HH:mm")
            : "—",
        key: uuidv4(),
      },
      {
        accessorKey: "createdBy",
        header: t("invoices:batch_session_created_by"),
        cell: ({ row }) =>
          row.original.createdBy
            ? `${row.original.createdBy.firstName} ${row.original.createdBy.lastName}`
            : "—",
        key: uuidv4(),
      },
      {
        accessorKey: "groupCount",
        header: t("invoices:batch_session_group_count"),
        cell: ({ row }) => row.original.groupCount ?? "—",
        key: uuidv4(),
      },
      {
        accessorKey: "itemCount",
        header: t("invoices:batch_session_item_count"),
        cell: ({ row }) => row.original.itemCount ?? "—",
        key: uuidv4(),
      },
    ],
    [t]
  );

  const table = useReactTable({
    data: isLoading ? Array(pagination.pageSize).fill({}) : sessions,
    columns: isLoading
      ? columns.map((column) => ({
          ...column,
          cell: () => <Skeleton className="h-4 w-[150px]" />,
        }))
      : columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: isLoading ? 0 : total,
    state: { pagination },
    initialState: { pagination },
  });

  return (
    <div className="relative">
      <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
        <div className="block md:flex justify-between pt-4 md:py-8 pr-8 items-center bg-grayLightMainBg">
          <div className="ml-8 text-2xl md:text-4xl pb-4 md:pb-0">
            {t("invoices:batch_sessions_title")}
          </div>
          <div className="pl-8 md:pl-0">
            <Link to="/admin/invoices">
              <Button>{t("button_back")}</Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="w-full px-8 py-4 bg-grayLightMainBg">
        <div className="rounded-md border p-2">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead className="pt-3 pb-3" key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell className="text-sm pt-3 pb-3" key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {t("invoices:batch_session_no_items")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <DataTablePagination table={table} />
      </div>
    </div>
  );
}
